import { createSealer } from './seal';
import { Batch, CollectorEvent } from './types';
import { now } from './util';

export interface TransportOptions {
  endpoint: string;
  key: string;
  sid: string;
  pid: string;
  version: string;
  flushIntervalMs: number;
  maxQueue?: number;
  /** Ingest public key (base64 SPKI). When set, every batch is encrypted. */
  publicKey?: string;
  keyId?: string;
}

export interface Transport {
  push: (e: CollectorEvent) => void;
  flush: (unloading?: boolean) => void;
  stop: () => void;
}

const PENDING_KEY = 'agentcatch_pending';
const PENDING_MAX_CHARS = 400_000;
const KEEPALIVE_MAX = 60_000; // keepalive bodies are capped at 64KB per page

const readPending = (): Batch[] => {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    return raw ? (JSON.parse(raw) as Batch[]) : [];
  } catch {
    return [];
  }
};

const writePending = (batches: Batch[]) => {
  try {
    if (!batches.length) {
      sessionStorage.removeItem(PENDING_KEY);
      return;
    }
    let json = JSON.stringify(batches);
    // Drop the oldest batches rather than blow the storage quota
    while (json.length > PENDING_MAX_CHARS && batches.length > 1) {
      batches = batches.slice(1);
      json = JSON.stringify(batches);
    }
    sessionStorage.setItem(PENDING_KEY, json);
  } catch {
    /* storage unavailable: lose the batch rather than break the page */
  }
};

/**
 * Queues events and sends them in batches. Bodies are text/plain so the request is a
 * CORS "simple request" (no preflight).
 *
 * Requests sent while a page unloads can be dropped (some browsers, including
 * enterprise ones, block beacons), so on unload the batch is also saved to
 * sessionStorage and the next page in the session re-sends it. Batches are
 * identified by pid + seq, so a batch that arrives twice can be de-duplicated.
 */
export const createTransport = (o: TransportOptions): Transport => {
  const url = `${o.endpoint.replace(/\/+$/, '')}/v1/events`;
  const maxQueue = o.maxQueue ?? 500;
  let queue: CollectorEvent[] = [];
  let seq = 0;

  const post = (body: string, keepalive: boolean) =>
    fetch(url, {
      method: 'POST',
      body,
      headers: { 'Content-Type': 'text/plain' },
      credentials: 'omit',
      mode: 'cors',
      keepalive,
    });

  // With a public key, batches go out encrypted or not at all. When they can't be
  // encrypted, one small plaintext marker per page says so (no events, just the reason):
  // otherwise deleting crypto.subtle would make a session vanish without trace.
  const sealer = o.publicKey ? createSealer(o.publicKey, o.keyId ?? 'k1') : undefined;
  let marked = false;
  const markUnsealed = (reason: string) => {
    if (marked) return;
    marked = true;
    const marker: Batch = {
      v: o.version, key: o.key, sid: o.sid, pid: o.pid, seq: seq++, sentAt: Date.now(),
      events: [{ type: 'unsealed', t: now(), reason }],
    };
    post(JSON.stringify(marker), true).catch(() => undefined);
  };
  const seal = (plain: string): Promise<string> | null => {
    if (sealer === undefined) return Promise.resolve(plain);
    if (sealer === null) {
      markUnsealed(typeof isSecureContext === 'boolean' && !isSecureContext ? 'insecure-context' : 'no-webcrypto');
      return null;
    }
    return sealer(plain).catch((e) => {
      markUnsealed('seal-failed');
      throw e;
    });
  };

  const deliver = (plain: string) => {
    if (sealer === undefined) post(plain, false).catch(() => undefined);
    else seal(plain)?.then((body) => post(body, false)).catch(() => undefined);
  };

  const send = (batch: Batch, unloading: boolean) => {
    if (!unloading) {
      deliver(JSON.stringify(batch));
      return;
    }
    // Saved in the clear in this tab's sessionStorage (never sent that way): sealing is
    // async, and the page may be gone before it finishes
    writePending([...readPending(), batch]);
    const beacon = (body: string) => {
      if (typeof navigator.sendBeacon === 'function') navigator.sendBeacon(url, new Blob([body], { type: 'text/plain' }));
    };
    if (sealer === undefined) {
      // Plaintext: stay synchronous so the request starts before the page goes
      const body = JSON.stringify({ ...batch, via: 'keepalive' });
      if (body.length < KEEPALIVE_MAX) post(body, true).catch(() => undefined);
      else beacon(JSON.stringify({ ...batch, via: 'beacon' }));
      return;
    }
    seal(JSON.stringify({ ...batch, via: 'keepalive' }))?.then(async (body) => {
      if (body.length < KEEPALIVE_MAX) await post(body, true);
      else beacon(await seal(JSON.stringify({ ...batch, via: 'beacon' }))!);
    }).catch(() => undefined);
  };

  const flush = (unloading = false) => {
    if (!queue.length) return;
    const batch: Batch = {
      v: o.version, key: o.key, sid: o.sid, pid: o.pid, seq: seq++, sentAt: Date.now(), events: queue,
    };
    queue = [];
    send(batch, unloading);
  };

  // Re-send whatever earlier pages in this session saved on unload
  const pending = readPending();
  if (pending.length) {
    writePending([]);
    for (const b of pending) deliver(JSON.stringify({ ...b, via: 'replay' }));
  }

  const timer = setInterval(() => flush(), o.flushIntervalMs);
  const onHide = () => flush(true);
  const onVisibility = () => {
    if (document.visibilityState === 'hidden') flush(true);
  };
  window.addEventListener('pagehide', onHide);
  document.addEventListener('visibilitychange', onVisibility);

  return {
    push: (e) => {
      queue.push(e);
      if (queue.length >= maxQueue) flush();
    },
    flush,
    stop: () => {
      clearInterval(timer);
      window.removeEventListener('pagehide', onHide);
      document.removeEventListener('visibilitychange', onVisibility);
      flush(true);
    },
  };
};
